import { goalAtRisk, type ContractListItem, type ForecastGap, type ForecastOverview, type InvoiceItem, type Issue, type MarginOverview, type Me, type OrderListItem, type Overview } from '@ft/shared';
import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Cust, Loading, StatusPill, Use, useLoad } from '../components/ui';
import { useI18n, type T } from '../i18n';
import { canSeePath, getAreaName } from '../lib/area';
import { dfmt, eur, keur, pct, skeur } from '../lib/format';
import { issueText } from './IssuesPage';
import { HealthPill } from './ProjectsPage';
import { Who } from '../lib/people';

/** Gehört ein offener Punkt zu einem Projekt? Über Projektname, Projekt-ID im Link oder eine Bestellung des Projekts. */
export function issueOfProject(i: Issue, p: { id: string; name: string }, orderIds: Set<string>): boolean {
  const pr = String(i.params.project ?? '');
  const prs = String(i.params.projects ?? '').split(', ');
  if (pr === p.name || prs.includes(p.name)) return true;
  if (i.link.includes(`projekt=${p.id}`)) return true;
  const m = /^\/bestellungen\/([0-9a-f-]{36})/.exec(i.link);
  return !!m && orderIds.has(m[1]!) && !i.params.project;
}

const isOpen = (o: OrderListItem) => o.kind === 'order' && o.volumeCents - o.billedCents > 100;

/** Eine Seite je Projekt: Kennzahlen, ein Satz mit dem, was zu tun ist, Bestellungen, Rechnungen, Marge, offene Punkte. */
export function ProjectPage({ me }: { me: Me }) {
  const { t } = useI18n();
  const nav = useNavigate();
  const { id } = useParams();
  const year = new Date().getFullYear();
  const see = (path: string) => canSeePath(me, path);
  const ov = useLoad<Overview>(`/api/overview?year=${year}`);
  const fc = useLoad<ForecastOverview>(see('/forecast') ? `/api/forecast?year=${year}` : null);
  const gap = useLoad<ForecastGap>(see('/invoicing') ? '/api/invoices/forecast-gap' : null);
  const orders = useLoad<OrderListItem[]>(see('/bestellungen') ? '/api/orders' : null);
  const invoices = useLoad<InvoiceItem[]>(see('/invoicing') ? '/api/invoices' : null);
  const margin = useLoad<MarginOverview>(me.role !== 'reader' ? '/api/margin' : null);
  const issues = useLoad<Issue[]>(see('/offene-punkte') ? '/api/issues' : null);
  const contracts = useLoad<ContractListItem[]>(see('/vertraege') ? '/api/contracts' : null);
  const [tab, setTab] = useState<'ov' | 'orders' | 'invoices' | 'margin' | 'issues'>('ov');

  const p = ov.data?.projects.find((x) => x.id === id) ?? null;
  const f = fc.data?.projects.find((x) => x.id === id) ?? null;
  const g = gap.data?.projects.find((x) => x.projectId === id) ?? null;
  const os = useMemo(() => (orders.data ?? []).filter((o) => o.projectIds.includes(id ?? '')), [orders.data, id]);
  const inv = useMemo(() => (invoices.data ?? []).filter((i) => i.project.id === id), [invoices.data, id]);
  const mg = margin.data?.projects.find((x) => x.project?.id === id) ?? null;
  const orderIds = useMemo(() => new Set(os.map((o) => o.id)), [os]);
  const iss = useMemo(() => (p ? (issues.data ?? []).filter((i) => issueOfProject(i, p, orderIds)) : []), [issues.data, p, orderIds]);
  const contract = p?.contract ? contracts.data?.find((c) => c.id === p.contract!.id) ?? null : null;

  if (!ov.data) return <section className="page"><Loading error={ov.error} /></section>;
  if (!p) return <section className="page"><div className="box empty">{t('pp.notFound')}</div></section>;

  const openOrders = os.filter(isOpen);
  const ending = iss.filter((i) => i.code === 'endingSoon');
  const endingIds = new Set(ending.map((i) => /^\/bestellungen\/([0-9a-f-]{36})/.exec(i.link)?.[1]).filter(Boolean));
  const overdue = inv.filter((i) => (i.payment?.overdueDays ?? 0) > 0 && !i.warranty);
  const lead = projectLead({ p, f, gapCents: g?.gapCents ?? 0, openOrders, endingIds, overdue, t });

  const marginPct = mg && margin.data ? (() => {
    const closed = new Set(margin.data.closedMonths);
    const xs = mg.months.filter((_, i) => closed.has(i + 1));
    const ns = xs.reduce((s, m) => s + m.netSalesCents, 0);
    const c = xs.reduce((s, m) => s + m.costCents, 0);
    return ns ? (ns - c) / ns : null;
  })() : null;
  const risk = goalAtRisk(p.targetCents, f?.expectedTotalCents ?? null);

  return (
    <section className="page">
      <div className="pagehead">
        <div>
          <div className="crumbs"><Link to="/projekte">{t('pz.title')}</Link><span>›</span><span>{p.customer.name}</span></div>
          <h1>{p.name}</h1>
          <div className="row small muted" style={{ marginTop: 4 }}>
            <Cust name={p.customer.name} />
            {p.workdayProjectId && <span>· {p.workdayProjectId}</span>}
            {p.pm && <span>· <Who id={p.pm.id} name={p.pm.name} initials={p.pm.initials} size={22} /></span>}
            {p.deputy && <span>· {t('pp.deputy', { name: p.deputy.name })}</span>}
            {p.contract && <span>· {t('pp.contract')} <Link to={`/vertraege/${p.contract.id}`}>{p.contract.name}</Link></span>}
            {risk ? <span className="pill neg">{t('pz.risk')}</span> : <HealthPill h={p.health} t={t} />}
          </div>
        </div>
        <span className="spacer" />
        {p.workdayProjectId && me.role !== 'reader' && <Link className="btn" to={`/marge/${p.workdayProjectId}`}>{t('pp.analysis')}</Link>}
        {see('/bestellungen') && <Link className="btn" to={`/bestellungen?projekt=${encodeURIComponent(p.name)}&status=alle`}>{t('pp.allOrders')}</Link>}
      </div>

      {lead.text && <p className={`say${lead.warn ? ' warn' : ''}`} dangerouslySetInnerHTML={{ __html: lead.text }} />}

      <div className="kpis six">
        <Kpi l={t('pp.kpi.target')} v={p.targetCents !== null ? eur(p.targetCents) : '–'} />
        <Kpi l={t('pp.kpi.ordered')} v={eur(p.orderedCents)} s={p.targetCents ? t('pp.ofTarget', { p: pct(p.orderedCents / p.targetCents) }) : undefined} />
        <Kpi l={t('pp.kpi.billed')} v={eur(p.billedCents)} s={p.targetCents ? t('pp.ofTargetPace', { p: pct(p.billedCents / p.targetCents), pace: pct(ov.data.pace) }) : undefined} />
        <Kpi l={t('pp.kpi.open')} v={eur(p.openCents)} s={orders.data ? t('pp.openOrders', { n: openOrders.length }) : undefined} />
        <Kpi l={t('pp.kpi.expected')} v={f ? eur(f.expectedTotalCents) : '–'} s={f && p.targetCents ? t('pp.toTarget', { d: skeur(f.expectedTotalCents - p.targetCents) }) : f ? undefined : t('pp.noForecast')} tone={f && p.targetCents ? (risk ? 'neg' : 'pos') : undefined} />
        <Kpi l={t('pp.kpi.margin')} v={marginPct !== null ? pct(marginPct) : '–'} s={mg ? t('pp.marginSub', { h: Math.round(mg.hoursX100 / 100).toLocaleString() }) : t('pp.noMargin')} />
      </div>

      <div className="ptabs" role="tablist">
        {([['ov', t('pp.tab.ov'), null], ['orders', t('pp.tab.orders'), orders.data ? os.length : null], ['invoices', t('pp.tab.invoices'), invoices.data ? inv.length : null], ['margin', t('pp.tab.margin'), null], ['issues', t('pp.tab.issues'), issues.data ? iss.length : null]] as const)
          .filter(([k]) => (k !== 'orders' || see('/bestellungen')) && (k !== 'invoices' || see('/invoicing')) && (k !== 'margin' || me.role !== 'reader') && (k !== 'issues' || see('/offene-punkte')))
          .map(([k, label, n]) => <button key={k} role="tab" aria-selected={tab === k} onClick={() => setTab(k)}>{label}{n !== null && <em>{n}</em>}</button>)}
      </div>

      {tab === 'ov' && (
        <div className="ppgrid">
          <div className="box">
            <div className="panel-h"><h2>{t('pp.chart', { y: year })}</h2></div>
            {f ? <MonthChart f={f} t={t} /> : <div className="empty">{t('pp.noForecast')}</div>}
          </div>
          <div className="ppside">
            {contract && (
              <div className="box pad">
                <h2 className="h2s">{t('pp.contract')}</h2>
                <Link to={`/vertraege/${contract.id}`} className="b">{contract.name}</Link>
                <div className="muted small">{[contract.contractNumber, contract.validFrom && contract.validTo ? `${dfmt(contract.validFrom)} – ${dfmt(contract.validTo)}` : null].filter(Boolean).join(' · ')}</div>
                {contract.budgetCents ? <>
                  <div className="meter" style={{ margin: '10px 0 6px' }}><i style={{ width: `${Math.min(100, (contract.orderedCents / contract.budgetCents) * 100)}%` }} /></div>
                  <div className="row small" style={{ justifyContent: 'space-between' }}><span>{t('pp.contractOrdered', { o: eur(contract.orderedCents), b: eur(contract.budgetCents) })}</span><b>{pct(contract.orderedCents / contract.budgetCents)}</b></div>
                  {contract.restCents !== null && <div className="muted small">{t('pp.contractFree', { r: eur(contract.restCents) })}</div>}
                </> : <div className="muted small" style={{ marginTop: 6 }}>{t('pp.contractNoBudget')}</div>}
              </div>
            )}
            {mg && (
              <div className="box pad">
                <h2 className="h2s">{t('pp.comment')}</h2>
                {(() => { const last = Object.keys(mg.comments).sort().pop(); return last
                  ? <><div className="small" style={{ whiteSpace: 'pre-wrap' }}>{mg.comments[last]}</div><div className="muted small">{last}</div></>
                  : <div className="muted small">{t('pp.noComment')}</div>; })()}
                {p.workdayProjectId && <Link className="small" to={`/marge/${p.workdayProjectId}`}>{t('pp.toComment')}</Link>}
              </div>
            )}
            {(f?.forecastFile || contract?.sharepointUrl) && (
              <div className="box pad">
                <h2 className="h2s">{t('pp.files')}</h2>
                {f?.forecastFile && <div className="small"><a href={f.forecastFile.url} target="_blank" rel="noreferrer">{t('pp.forecastFile')}</a>{f.importedAt && <span className="muted"> · {t('pp.asOf', { d: dfmt(f.importedAt) })}</span>}</div>}
                {contract?.sharepointUrl && <div className="small"><a href={contract.sharepointUrl} target="_blank" rel="noreferrer">{t('pp.contractFile')}</a></div>}
              </div>
            )}
          </div>
        </div>
      )}

      {tab === 'orders' && (
        <div className="box tablewrap">
          {!orders.data ? <Loading error={orders.error} /> : !os.length ? <div className="empty">{t('pp.noOrders')}</div> : (
            <>
              <OrderTable list={os.filter((o) => o.status !== 'settled')} endingIds={endingIds} onOpen={(oid) => nav(`/bestellungen/${oid}`)} t={t} />
              {os.some((o) => o.status === 'settled') && (
                <details className="more"><summary>{t('pp.settledShow', { n: os.filter((o) => o.status === 'settled').length })}</summary>
                  <OrderTable list={os.filter((o) => o.status === 'settled')} endingIds={endingIds} onOpen={(oid) => nav(`/bestellungen/${oid}`)} t={t} muted />
                </details>
              )}
            </>
          )}
        </div>
      )}

      {tab === 'invoices' && (
        <div className="box tablewrap">
          {!invoices.data ? <Loading error={invoices.error} /> : !inv.length ? <div className="empty">{t('pp.noInvoices')}</div> : (
            <table>
              <thead><tr><th>{t('pp.col.invoice')}</th><th>{t('pp.col.date')}</th><th>{t('pp.col.order')}</th><th className="r">{t('pp.col.net')}</th><th>{t('pp.col.payment')}</th></tr></thead>
              <tbody>{inv.map((i) => (
                <tr key={i.id} className="click" tabIndex={0} onClick={() => nav(`/invoicing?q=${encodeURIComponent(i.invoiceNumber)}`)}>
                  <td>{i.invoiceNumber}{i.warranty && <span className="tag">{t('status.warranty')}</span>}</td>
                  <td className="num">{dfmt(i.invoiceDate)}</td>
                  <td>{i.order?.orderNumber ?? <span className="muted">–</span>}</td>
                  <td className="r num" style={i.amountCents < 0 ? { color: 'var(--neg)' } : undefined}>{eur(i.amountCents)}</td>
                  <td>{!i.payment ? <span className="muted">–</span> : i.payment.status === 'paid' ? <span className="pill pos">{t('pp.paid')}</span>
                    : (i.payment.overdueDays ?? 0) > 0 ? <span className="pill neg">{t('pp.overdue', { n: i.payment.overdueDays ?? 0 })}</span> : <span className="pill crit">{t('pp.unpaid')}</span>}</td>
                </tr>))}</tbody>
            </table>
          )}
        </div>
      )}

      {tab === 'margin' && (
        <div className="box tablewrap">
          {!margin.data ? <Loading error={margin.error} /> : !mg ? <div className="empty">{t('pp.noMargin')}</div> : <MarginTable mg={mg} closed={margin.data.closedMonths} target={(mg.target ?? margin.data.areaTarget) / 100} t={t} />}
        </div>
      )}

      {tab === 'issues' && (
        <div className="box">
          {!iss.length ? <div className="empty">{t('issues.empty')}</div> : (
            <ul className="issues">{iss.map((i) => { const x = issueText(i, t); return (
              <li key={i.key}>
                <span><span className={`pill ${i.level === 'neg' ? 'neg' : i.level === 'crit' ? 'crit' : 'neu'}`}>{t(`issues.level.${i.level}`)}</span></span>
                <div><div className="t">{x.title}</div><div className="muted small">{x.detail}</div></div>
                <div className="row"><Link className="btn" to={i.link}>{t('common.open')}</Link></div>
              </li>); })}</ul>
          )}
          <div className="panel-f"><Link to="/offene-punkte">{t('pp.allIssues')}</Link></div>
        </div>
      )}
    </section>
  );
}

function Kpi({ l, v, s, tone }: { l: string; v: string; s?: string; tone?: 'pos' | 'neg' }) {
  return <div className="kpi"><span className="lbl">{l}</span><span className="val num">{v}</span>{s && <span className="sub" style={tone ? { color: `var(--${tone})` } : undefined}>{s}</span>}</div>;
}

function OrderTable({ list, endingIds, onOpen, t, muted }: { list: OrderListItem[]; endingIds: Set<string | undefined>; onOpen: (id: string) => void; t: T; muted?: boolean }) {
  return (
    <table>
      <thead><tr><th>{t('orders.col.number')}</th><th>{t('orders.col.orderedOn')}</th><th>{t('orders.col.lastInvoice')}</th><th className="r">{t('orders.col.volume')}</th><th className="r">{t('orders.col.billed')}</th><th className="r">{t('orders.col.rest')}</th><th>{t('orders.col.usage')}</th><th>{t('common.status')}</th></tr></thead>
      <tbody>{list.map((o) => (
        <tr key={o.id} className={`click${muted ? ' mutedrow' : ''}`} tabIndex={0} onClick={() => onOpen(o.id)} onKeyDown={(e) => { if (e.key === 'Enter') onOpen(o.id); }}>
          <td><span className="idlink">{o.orderNumber ?? t('common.noNumber')}</span>{endingIds.has(o.id) && <span className="pill crit" style={{ marginLeft: 6 }}>{t('orders.endsSoon')}</span>}</td>
          <td className="num">{dfmt(o.orderDate)}</td>
          <td className="num">{dfmt(o.lastInvoiceDate)}</td>
          <td className="r num">{eur(o.volumeCents)}</td>
          <td className="r num">{eur(o.billedCents)}</td>
          <td className="r num"><Rest o={o} t={t} /></td>
          <td>{o.kind === 'order' ? <Use volume={o.volumeCents} billed={o.billedCents} /> : <span className="muted">–</span>}</td>
          <td><StatusPill s={o.status} /></td>
        </tr>))}</tbody>
    </table>
  );
}

/** Rest einer Bestellung; bei Gewährleistung über Bestellwert: „1.850 € mehr abgerechnet“ statt eines negativen Rests. */
export function Rest({ o, t }: { o: OrderListItem; t: T }) {
  const rest = o.volumeCents - o.billedCents;
  if (rest < 0 && o.kind === 'warranty') return <span className="muted" title={t('orders.overBilledHint', { b: eur(o.billedCents), v: eur(o.volumeCents) })}>{t('orders.overBilled', { x: eur(-rest) })}</span>;
  return <span style={rest < 0 ? { color: 'var(--neg)' } : undefined}>{eur(rest)}</span>;
}

function MonthChart({ f, t }: { f: ForecastOverview['projects'][number]; t: T }) {
  const W = 720, H = 220, padL = 34, bw = (W - padL) / 12;
  const max = Math.max(1, ...f.forecastCents.map((x) => x ?? 0), ...f.actualCents) * 1.1;
  const step = niceStep(max / 100 / 4);
  const months = t('pp.months').split(',');
  const now = new Date().getMonth();
  const y = (c: number) => H - 24 - (H - 40) * (Math.max(0, c) / max);
  return (
    <div style={{ padding: '8px 14px 12px' }}>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} role="img" aria-label={t('pp.chartLabel')}>
        {[0, 1, 2, 3, 4].map((k) => { const c = k * step * 100; if (c > max) return null; return <g key={k}><line x1={padL} x2={W} y1={y(c)} y2={y(c)} stroke="var(--line)" /><text x={0} y={y(c) + 4} fill="var(--faint)" fontSize="10">{keur(c)}</text></g>; })}
        {months.map((m, i) => {
          const x = padL + i * bw + 5, w = bw / 2 - 5;
          const fcv = f.forecastCents[i] ?? 0, act = f.actualCents[i] ?? 0;
          return (
            <g key={i}>
              <rect x={x} y={y(fcv)} width={w} height={H - 24 - y(fcv)} rx={3} fill="var(--ns-bar)" opacity={i >= now ? 0.55 : 1}><title>{t('pp.fcOf', { m, v: eur(fcv) })}</title></rect>
              <rect x={x + w + 2} y={y(act)} width={w} height={H - 24 - y(act)} rx={3} fill="var(--accent)"><title>{t('pp.actOf', { m, v: eur(act) })}</title></rect>
              <text x={x + w} y={H - 8} fill="var(--muted)" fontSize="11" textAnchor="middle">{m}</text>
            </g>
          );
        })}
      </svg>
      <div className="legend"><span><i className="lg" style={{ background: 'var(--ns-bar)' }} />{t('pp.legend.fc')}</span><span><i className="lg" style={{ background: 'var(--accent)' }} />{t('pp.legend.act')}</span></div>
    </div>
  );
}

function niceStep(raw: number): number {
  const p = 10 ** Math.floor(Math.log10(Math.max(1, raw)));
  const n = raw / p;
  return (n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10) * p;
}

function MarginTable({ mg, closed, target, t }: { mg: MarginOverview['projects'][number]; closed: number[]; target: number; t: T }) {
  const months = t('pp.months').split(',');
  const rows = mg.months.map((m, i) => ({ ...m, i })).filter((m) => closed.includes(m.i + 1));
  const avgH = rows.length ? rows.reduce((s, m) => s + m.hoursX100, 0) / rows.length : 0;
  const ns = rows.reduce((s, m) => s + m.netSalesCents, 0), c = rows.reduce((s, m) => s + m.costCents, 0), h = rows.reduce((s, m) => s + m.hoursX100, 0);
  return (
    <table>
      <thead><tr><th>{t('pp.col.month')}</th><th className="r">{t('mg.kpi.ns')}</th><th className="r">{t('mg.kpi.cost')}</th><th className="r">{t('pp.col.margin')}</th><th className="r">{t('mg.kpi.hours')}</th><th /></tr></thead>
      <tbody>{rows.map((m) => { const mp = m.netSalesCents ? (m.netSalesCents - m.costCents) / m.netSalesCents : null; return (
        <tr key={m.i}><td>{months[m.i]}</td><td className="r num">{eur(m.netSalesCents)}</td><td className="r num">{eur(m.costCents)}</td>
          <td className="r num" style={{ color: mp === null ? undefined : mp < target ? 'var(--crit)' : 'var(--pos)' }}>{mp === null ? '–' : pct(mp)}</td>
          <td className="r num">{Math.round(m.hoursX100 / 100).toLocaleString()} h</td>
          <td className="muted small">{avgH && m.hoursX100 > avgH * 1.15 ? t('pp.moreHours', { h: Math.round(avgH / 100).toLocaleString() }) : ''}</td></tr>); })}</tbody>
      <tfoot><tr><td>{t('common.sum')}</td><td className="r num">{eur(ns)}</td><td className="r num">{eur(c)}</td><td className="r num">{ns ? pct((ns - c) / ns) : '–'}</td><td className="r num">{Math.round(h / 100).toLocaleString()} h</td><td /></tr></tfoot>
    </table>
  );
}

/** Der Satz oben: Ziel, Erwartung und was zu tun ist. HTML nur mit eigenen, formatierten Werten (keine Nutzereingaben). */
export function projectLead({ p, f, gapCents, openOrders, endingIds, overdue, t }: {
  p: Overview['projects'][number]; f: ForecastOverview['projects'][number] | null; gapCents: number;
  openOrders: OrderListItem[]; endingIds: Set<string | undefined>; overdue: InvoiceItem[]; t: T;
}): { text: string; warn: boolean } {
  const b = (s: string) => `<b>${s}</b>`;
  const parts: string[] = [];
  let warn = false;
  if (p.targetCents) {
    const ordered = t('pp.say.ordered', { p: b(pct(p.orderedCents / p.targetCents)) });
    if (f) {
      const d = f.expectedTotalCents - p.targetCents;
      parts.push(goalAtRisk(p.targetCents, f.expectedTotalCents)
        ? t('pp.say.miss', { ordered, exp: b(keur(f.expectedTotalCents)), d: b(skeur(d)) })
        : t('pp.say.reach', { ordered, exp: b(keur(f.expectedTotalCents)) }));
      if (goalAtRisk(p.targetCents, f.expectedTotalCents)) warn = true;
    } else parts.push(`${ordered}.`);
  }
  if (f) {
    const ye = f.forecastRestCents + Math.max(0, gapCents);
    const open = openOrders.reduce((s, o) => s + o.volumeCents - o.billedCents, 0);
    if (open > ye + 100_000) {
      warn = true;
      parts.push(t('pp.say.unused', { open: b(keur(open)), ye: b(keur(ye)), rest: keur(open - ye) }));
      const end = openOrders.filter((o) => endingIds.has(o.id));
      if (end.length) parts.push(t('pp.say.ending', { n: end.length, m: openOrders.length, sum: keur(end.reduce((s, o) => s + o.volumeCents - o.billedCents, 0)), act: b(t('pp.say.extend')) }));
    } else if (f.uncoveredCents > 100_000) {
      warn = true;
      parts.push(t('pp.say.uncovered', { x: b(keur(f.uncoveredCents)), act: b(t('pp.say.followUp')) }));
    }
  }
  if (overdue.length) {
    warn = true;
    parts.push(t('pp.say.overdue', { n: overdue.length, sum: b(keur(overdue.reduce((s, i) => s + (i.payment?.openNetCents ?? 0), 0))) }));
  }
  return { text: parts.join(' '), warn };
}
